import { useEffect, useRef, useState, type ReactNode } from "react";
import styles from "./draggableWindow.module.scss";

interface DraggableWindowProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: number;
  height?: number;
}

export function DraggableWindow({
  open,
  onClose,
  title,
  children,
  width = 480,
  height = 600,
}: DraggableWindowProps) {
  const windowRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ 
    x: (typeof window !== 'undefined' ? window.innerWidth - width : 0) / 2, 
    y: (typeof window !== 'undefined' ? window.innerHeight - height : 0) / 2 
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!windowRef.current) return;
    setIsDragging(true);
    const rect = windowRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  useEffect(() => {
    if (!isDragging) return;

    function handleMouseMove(e: MouseEvent) {
      const newX = e.clientX - dragOffsetRef.current.x;
      const newY = e.clientY - dragOffsetRef.current.y;

      // Keep window within viewport
      const maxX = window.innerWidth - 50;
      const maxY = window.innerHeight - 50;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }

    function handleMouseUp() {
      setIsDragging(false);
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Recenter within the viewport each time the window opens. The rendered width
  // is capped (see .window max-width), so when `width` exceeds a small viewport
  // the naive centered `left` would be negative and open the window off-screen
  // to the left. Clamp the size to the viewport and keep a small margin.
  useEffect(() => {
    if (!open) return;
    const margin = 8;
    const w = Math.min(width, window.innerWidth - margin * 2);
    const h = Math.min(height, window.innerHeight - margin * 2);
    setPosition({
      x: Math.max(margin, Math.round((window.innerWidth - w) / 2)),
      y: Math.max(margin, Math.round((window.innerHeight - h) / 2)),
    });
  }, [open, width, height]);

  if (!open) return null;

  return (
    <div
      ref={windowRef}
      className={styles.window}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${width}px`,
        height: `${height}px`,
      }}
    >
      <div
        ref={headerRef}
        className={styles.header}
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        <h2 className={styles.title}>{title}</h2>
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          &times;
        </button>
      </div>
      <div className={styles.body}>{children}</div>
    </div>
  );
}
